import React, { useMemo, useRef } from 'react'
import Icon from '../../Icon'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import datepickerStyles from '../datepicker.module.scss'
import styles from '../../FormControls/form-controls.module.scss'
import { Button, TextInput } from '../../index'
import Container from '../../Container'
import style from './button.module.scss'

const DatePickerButton = ({
  asButton = false,
  isOpen = false,
  value = {},
  bindTrigger,
  error = null,
  helperText = null,
  fullWidth = false,
  disabled = false,
  readOnly = false,
  hidden = false,
  label = '',
  size = 'l',
  ControlProps = { buttonColor: 'text-secondary' },
}) => {
  const InputRef = useRef()
  const isFocused = useMemo(() => isOpen, [isOpen])

  const endAdornment = useMemo(
    () => (
      <>
        {ControlProps.endAdornment}
        <div className={styles['control__triggerButtonContainer']}>
          <Icon
            size={size === 's' ? 's' : 'm'}
            name={'calendar'}
            color={
              disabled || readOnly ? 'Label-Quaternary' : 'Interface-Seventh'
            }
          />
        </div>
      </>
    ),
    [disabled, readOnly, size],
  )

  return asButton ? (
    <Container
      className={clsx(
        styles['control-component'],
        styles['control-component_variant_outlined'],
        styles[`control-component_size_${size}`],
        {
          [styles['control-component_focused']]: isFocused,
        },
      )}
    >
      <Container className={clsx(styles['control__controlWrapper'])}>
        <Button
          {...bindTrigger}
          error={error}
          size={size}
          hidden={hidden}
          label={label}
          fullWidth
          variant={'ghost'}
          color={'inherit'}
          className={style['button']}
          endIcon={
            <Icon
              size={size === 's' ? 's' : 'm'}
              name={'calendar'}
              color={'Interface-Seventh'}
            />
          }
        >
          {typeof value === 'string'
            ? value
            : `${value.startDate} — ${value.endDate}`}
        </Button>
      </Container>
    </Container>
  ) : (
    <Container flexGrow={fullWidth ? '1' : 'auto'} {...bindTrigger}>
      <TextInput
        error={error}
        focus={isOpen}
        ref={InputRef}
        size={size}
        autocomplete={'off'}
        readOnly
        hidden={hidden}
        label={label}
        helperText={helperText}
        value={
          typeof value === 'string'
            ? value
            : `${value.startDate} — ${value.endDate}`
        }
        fullWidth
        InputProps={{
          endAdornment: endAdornment,
          className: clsx(datepickerStyles['button'], {
            [styles['control_overflow_visible']]: asButton,
          }),
        }}
      />
    </Container>
  )
}

DatePickerButton.propTypes = {
  asButton: PropTypes.bool,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  compactButton: PropTypes.bool,
  isOpen: PropTypes.bool,
  className: PropTypes.string,
  classNameOpened: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  bindTrigger: PropTypes.object,
  startIcon: PropTypes.element,
  endIcon: PropTypes.element,
  error: PropTypes.oneOf([null, PropTypes.string]),
  helperText: PropTypes.oneOf([null, PropTypes.string]),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  style: PropTypes.object,
  readOnly: PropTypes.bool,
  hidden: PropTypes.bool,
  label: PropTypes.string,
  size: PropTypes.oneOf(['s', 'm', 'l']),
  ControlProps: PropTypes.object,
}

export default DatePickerButton
